import { useMutation, useQueryClient } from "@tanstack/react-query";
import { recordInstanceKeys } from "../record/record.options";
import { ticketKeys } from "./ticket.options";
import { ticketService } from "./ticket.service";
import type {
	CreateTicketInfo,
	CreateTicketTypeInfo,
	EditTicketInfo,
	EditTicketTypeInfo,
	ServiceResult,
	TicketFromTextRequest,
	TicketParams,
	TicketTypesQueryOptionsInput,
	UpdateTicketStatusInfo,
} from "./ticket.types";

// ==================== CORE TICKET MUTATIONS ====================

/**
 * Create a new ticket with enhanced error handling and optimistic updates
 */
export const useCreateTicket = (pageSize: number, pageIndex: number) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateTicketInfo) => ticketService.createTicket(data),
		onMutate: async (newTicket) => {
			// Cancel any outgoing refetches
			await queryClient.cancelQueries({
				queryKey: ticketKeys.tickets(),
			});

			// Snapshot the previous value
			const previousTickets = queryClient.getQueriesData({
				queryKey: ticketKeys.tickets(),
			});

			// Optimistically update the cache
			queryClient.setQueriesData(
				{ queryKey: ticketKeys.ticketsList(pageSize, pageIndex) },
				(old: unknown) => {
					if (!old || typeof old !== "object") return old;
					const oldData = old as { data?: unknown[] };
					return {
						...oldData,
						data: [
							...(oldData.data || []),
							{
								id: `temp-${Date.now()}`,
								createdAt: new Date().toISOString(),
								...newTicket,
							},
						],
					};
				},
			);

			return { previousTickets };
		},
		onSuccess: (result: ServiceResult) => {
			// Invalidate tickets list queries
			queryClient.invalidateQueries({
				queryKey: ticketKeys.tickets(),
			});

			// If we have the affected ID, we can be more specific
			if (result.affectedId) {
				queryClient.invalidateQueries({
					queryKey: ticketKeys.ticketsDetail(result.affectedId),
				});
			}
			return result;
		},
		onError: (error, _newTicket, context) => {
			// Revert optimistic update on error
			if (context?.previousTickets) {
				context.previousTickets.forEach(([queryKey, data]) => {
					queryClient.setQueryData(queryKey, data);
				});
			}

			console.error("Failed to create ticket:", error);
		},
		onSettled: () => {
			// Always refetch after error or success
			queryClient.invalidateQueries({
				queryKey: ticketKeys.tickets(),
			});
		},
	});
};

// ==================== TICKET FROM TEXT MUTATIONS ====================

/**
 * Preview a ticket drafted from free text.
 * POST /tickets/from-text/preview
 */
export const usePreviewTicketFromText = () => {
	return useMutation({
		mutationFn: (data: TicketFromTextRequest) =>
			ticketService.previewTicketFromText(data),
		onError: (error) => {
			console.error("Failed to preview ticket from text:", error);
		},
	});
};

/**
 * Create a ticket drafted from free text, then refresh ticket lists.
 * POST /tickets/from-text
 */
export const useCreateTicketFromText = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: TicketFromTextRequest) =>
			ticketService.createTicketFromText(data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ticketKeys.tickets(),
			});
		},
		onError: (error) => {
			console.error("Failed to create ticket from text:", error);
		},
	});
};

// ==================== UPDATE TICKET MUTATION ====================

/**
 * Update a ticket with cache invalidation
 */
export const useUpdateTicket = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: EditTicketInfo }) =>
			ticketService.updateTicket(id, data),
		onSuccess: (result: ServiceResult, { id }) => {
			// Invalidate the specific ticket detail query
			queryClient.invalidateQueries({
				queryKey: ticketKeys.ticketsDetail(id),
			});

			// Invalidate all ticket list queries
			queryClient.invalidateQueries({
				queryKey: ticketKeys.tickets(),
			});

			// Invalidate ticket assignments (in case organizationUserIds was updated)
			queryClient.invalidateQueries({
				queryKey: ticketKeys.ticketAssignments(id),
			});

			// Invalidate ticket sub-contractors (in case subContractorIds was updated)
			queryClient.invalidateQueries({
				queryKey: ticketKeys.ticketSubContractors(id),
			});

			return result;
		},
		onError: (error) => {
			console.error("Failed to update ticket:", error);
		},
	});
};

// ==================== UPDATE TICKET STATUS MUTATION ====================

/**
 * Update a ticket's status with cache invalidation
 */
export const useUpdateTicketStatus = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateTicketStatusInfo }) =>
			ticketService.updateTicketStatus(id, data),
		onSuccess: (result: ServiceResult, { id }) => {
			// Invalidate the specific ticket detail query
			queryClient.invalidateQueries({
				queryKey: ticketKeys.ticketsDetail(id),
			});

			// Invalidate all ticket list queries
			queryClient.invalidateQueries({
				queryKey: ticketKeys.tickets(),
			});

			// Invalidate valid statuses for this ticket
			queryClient.invalidateQueries({
				queryKey: ticketKeys.ticketsValidStatuses(id),
			});

			return result;
		},
		onError: (error) => {
			console.error("Failed to update ticket status:", error);
		},
	});
};

// ==================== DELETE TICKET MUTATION ====================

/**
 * Delete a ticket with cache invalidation
 */
/**
 * Delete a ticket with cache invalidation.
 * Pass `organizationId` (from the route) so platform admins scope the DELETE correctly.
 */
export const useDeleteTicket = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			id,
			organizationId,
		}: {
			id: string;
			organizationId?: string;
		}) => ticketService.deleteTicket(id, { organizationId }),
		onSuccess: (_result, { id }) => {
			queryClient.invalidateQueries({
				queryKey: ticketKeys.ticketsDetail(id),
			});
			queryClient.invalidateQueries({
				queryKey: ticketKeys.tickets(),
			});
			queryClient.invalidateQueries({
				queryKey: ticketKeys.ticketAssignments(id),
			});
			queryClient.invalidateQueries({
				queryKey: ticketKeys.ticketSubContractors(id),
			});
		},
		onError: (error) => {
			console.error("Failed to delete ticket:", error);
		},
	});
};

// ==================== CREATE TICKET TYPE ====================

/**
 * Create a ticket type with cache invalidation
 */
export const useCreateTicketType = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			data,
			organizationId,
		}: {
			data: CreateTicketTypeInfo;
			organizationId?: string;
		}) => ticketService.createTicketType(data, { organizationId }),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ticketKeys.ticketsTypes(),
			});
			queryClient.invalidateQueries({
				queryKey: [...ticketKeys.tickets(), "ticket-types"],
			});
			queryClient.invalidateQueries({
				queryKey: [...ticketKeys.tickets(), "ticket-type-detail"],
			});
		},
		onError: (error) => {
			console.error("Failed to create ticket type:", error);
		},
	});
};

/**
 * Duplicate a ticket type. Cache refresh matches create; navigation stays with the caller.
 */
export const useDuplicateTicketType = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			ticketTypeId,
			organizationId,
		}: {
			ticketTypeId: string;
			organizationId?: string;
		}) => ticketService.duplicateTicketType(ticketTypeId, { organizationId }),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ticketKeys.ticketsTypes(),
			});
			queryClient.invalidateQueries({
				queryKey: [...ticketKeys.tickets(), "ticket-types"],
			});
			queryClient.invalidateQueries({
				queryKey: [...ticketKeys.tickets(), "ticket-type-detail"],
			});
		},
		onError: (error) => {
			console.error("Failed to duplicate ticket type:", error);
		},
	});
};

// ==================== UPDATE TICKET TYPE ====================

export const useUpdateTicketType = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			ticketTypeId,
			data,
			organizationId,
		}: {
			ticketTypeId: string;
			data: EditTicketTypeInfo;
			organizationId?: string;
		}) =>
			ticketService.updateTicketType(ticketTypeId, data, { organizationId }),
		onSuccess: (_result, { ticketTypeId }) => {
			queryClient.invalidateQueries({
				queryKey: [...ticketKeys.tickets(), "ticket-types"],
			});
			queryClient.invalidateQueries({
				queryKey: [...ticketKeys.tickets(), "ticket-type-detail", ticketTypeId],
			});
			queryClient.invalidateQueries({
				queryKey: ticketKeys.ticketsTypes(),
			});
			queryClient.invalidateQueries({
				queryKey: ticketKeys.ticketTypeHistory(ticketTypeId),
			});
		},
		onError: (error) => {
			console.error("Failed to update ticket type:", error);
		},
	});
};

export const useEnableTicketType = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			ticketTypeId,
			organizationId,
		}: {
			ticketTypeId: string;
			organizationId?: string;
		}) => ticketService.enableTicketType(ticketTypeId, { organizationId }),
		onSuccess: (_result, { ticketTypeId }) => {
			queryClient.invalidateQueries({
				queryKey: [...ticketKeys.tickets(), "ticket-types"],
			});
			queryClient.invalidateQueries({
				queryKey: [...ticketKeys.tickets(), "ticket-type-detail", ticketTypeId],
			});
			queryClient.invalidateQueries({ queryKey: ticketKeys.ticketsTypes() });
			queryClient.invalidateQueries({
				queryKey: ticketKeys.ticketTypeHistory(ticketTypeId),
			});
		},
		onError: (error) => {
			console.error("Failed to enable ticket type:", error);
		},
	});
};

export const useDisableTicketType = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			ticketTypeId,
			organizationId,
		}: {
			ticketTypeId: string;
			organizationId?: string;
		}) => ticketService.disableTicketType(ticketTypeId, { organizationId }),
		onSuccess: (_result, { ticketTypeId }) => {
			queryClient.invalidateQueries({
				queryKey: [...ticketKeys.tickets(), "ticket-types"],
			});
			queryClient.invalidateQueries({
				queryKey: [...ticketKeys.tickets(), "ticket-type-detail", ticketTypeId],
			});
			queryClient.invalidateQueries({ queryKey: ticketKeys.ticketsTypes() });
			queryClient.invalidateQueries({
				queryKey: ticketKeys.ticketTypeHistory(ticketTypeId),
			});
		},
		onError: (error) => {
			console.error("Failed to disable ticket type:", error);
		},
	});
};

export const useEnableTicketTypeSubOrganization = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			typeId,
			subOrgId,
			organizationId,
		}: {
			typeId: string;
			subOrgId: string;
			organizationId?: string;
		}) =>
			ticketService.enableTicketTypeSubOrganization(typeId, subOrgId, {
				organizationId,
			}),
		onSuccess: (_result, { typeId }) => {
			queryClient.invalidateQueries({
				queryKey: [...ticketKeys.tickets(), "ticket-types"],
			});
			queryClient.invalidateQueries({
				queryKey: [...ticketKeys.tickets(), "ticket-type-detail", typeId],
			});
			queryClient.invalidateQueries({ queryKey: ticketKeys.ticketsTypes() });
			queryClient.invalidateQueries({
				queryKey: ticketKeys.ticketTypeHistory(typeId),
			});
		},
		onError: (error) => {
			console.error(
				"Failed to enable sub-organization for ticket type:",
				error,
			);
		},
	});
};

export const useDisableTicketTypeSubOrganization = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			typeId,
			subOrgId,
			organizationId,
		}: {
			typeId: string;
			subOrgId: string;
			organizationId?: string;
		}) =>
			ticketService.disableTicketTypeSubOrganization(typeId, subOrgId, {
				organizationId,
			}),
		onSuccess: (_result, { typeId }) => {
			queryClient.invalidateQueries({
				queryKey: [...ticketKeys.tickets(), "ticket-types"],
			});
			queryClient.invalidateQueries({
				queryKey: [...ticketKeys.tickets(), "ticket-type-detail", typeId],
			});
			queryClient.invalidateQueries({ queryKey: ticketKeys.ticketsTypes() });
			queryClient.invalidateQueries({
				queryKey: ticketKeys.ticketTypeHistory(typeId),
			});
		},
		onError: (error) => {
			console.error(
				"Failed to disable sub-organization for ticket type:",
				error,
			);
		},
	});
};

// ==================== EXPORT TICKET TYPES ====================

/**
 * Export ticket types to CSV
 */
export const useExportTicketTypes = () => {
	return useMutation({
		mutationFn: async ({
			startIndex,
			count,
			params,
		}: {
			startIndex: number;
			count: number;
			params: TicketTypesQueryOptionsInput & { columns?: string };
		}) => {
			const { columns, ...filters } = params;
			return await ticketService.exportTicketTypesCsv(
				startIndex,
				count,
				filters,
				columns,
			);
		},
		onError: (error) => {
			console.error("Ticket types export failed:", error);
		},
	});
};

// ==================== DELETE TICKET TYPE ====================

export const useDeleteTicketType = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			id,
			organizationId,
		}: {
			id: string;
			organizationId?: string;
		}) => ticketService.deleteTicketType(id, { organizationId }),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [...ticketKeys.tickets(), "ticket-types"],
			});
			queryClient.invalidateQueries({
				queryKey: [...ticketKeys.tickets(), "ticket-type-detail"],
			});
			queryClient.invalidateQueries({
				queryKey: ticketKeys.ticketsTypes(),
			});
		},
		onError: (error) => {
			console.error("Failed to delete ticket type:", error);
		},
	});
};

// ==================== EXPORT MUTATION ====================

/**
 * Export tickets to CSV
 */
export const useExportTickets = () => {
	return useMutation({
		mutationFn: async ({
			startIndex,
			count,
			params,
		}: {
			startIndex: number;
			count: number;
			params: TicketParams & { columns?: string };
		}) => {
			const blob = await ticketService.exportTickets(startIndex, count, params);
			return blob;
		},
		onError: (error) => {
			console.error("Export failed:", error);
		},
	});
};

// ==================== TICKET ASSET / RECORD ASSOCIATION MUTATIONS ====================

/**
 * Link an asset to a ticket.
 * POST /tickets/{ticketId}/assets/{assetId}
 */
export const useLinkTicketAsset = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			ticketId,
			assetId,
			organizationId,
		}: {
			ticketId: string;
			assetId: string;
			organizationId?: string;
		}) => ticketService.linkTicketAsset(ticketId, assetId, { organizationId }),
		onSuccess: (_result, { ticketId }) => {
			void Promise.all([
				queryClient.invalidateQueries({
					queryKey: ticketKeys.ticketAssets(ticketId),
				}),
				queryClient.invalidateQueries({
					queryKey: ticketKeys.ticketsDetail(ticketId),
				}),
				queryClient.invalidateQueries({ queryKey: ["asset-instance"] }),
			]);
		},
	});
};

/**
 * Unlink an asset from a ticket.
 * DELETE /tickets/{ticketId}/assets/{assetId}
 */
export const useUnlinkTicketAsset = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			ticketId,
			assetId,
			organizationId,
		}: {
			ticketId: string;
			assetId: string;
			organizationId?: string;
		}) =>
			ticketService.unlinkTicketAsset(ticketId, assetId, { organizationId }),
		onSuccess: (_result, { ticketId }) => {
			void Promise.all([
				queryClient.invalidateQueries({
					queryKey: ticketKeys.ticketAssets(ticketId),
				}),
				queryClient.invalidateQueries({
					queryKey: ticketKeys.ticketsDetail(ticketId),
				}),
				queryClient.invalidateQueries({ queryKey: ["asset-instance"] }),
			]);
		},
	});
};

/**
 * Link a record to a ticket.
 * POST /tickets/{ticketId}/records/{recordId}
 */
export const useLinkTicketRecord = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			ticketId,
			recordId,
			organizationId,
		}: {
			ticketId: string;
			recordId: string;
			organizationId?: string;
		}) =>
			ticketService.linkTicketRecord(ticketId, recordId, { organizationId }),
		onSuccess: (_result, { ticketId }) => {
			void Promise.all([
				queryClient.invalidateQueries({
					queryKey: ticketKeys.ticketRecords(ticketId),
				}),
				queryClient.invalidateQueries({
					queryKey: ticketKeys.ticketsDetail(ticketId),
				}),
				queryClient.invalidateQueries({
					queryKey: recordInstanceKeys.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: recordInstanceKeys.counts(),
				}),
			]);
		},
	});
};

/**
 * Unlink a record from a ticket.
 * DELETE /tickets/{ticketId}/records/{recordId}
 */
export const useUnlinkTicketRecord = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			ticketId,
			recordId,
			organizationId,
		}: {
			ticketId: string;
			recordId: string;
			organizationId?: string;
		}) =>
			ticketService.unlinkTicketRecord(ticketId, recordId, { organizationId }),
		onSuccess: (_result, { ticketId }) => {
			void Promise.all([
				queryClient.invalidateQueries({
					queryKey: ticketKeys.ticketRecords(ticketId),
				}),
				queryClient.invalidateQueries({
					queryKey: ticketKeys.ticketsDetail(ticketId),
				}),
				queryClient.invalidateQueries({
					queryKey: recordInstanceKeys.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: recordInstanceKeys.counts(),
				}),
			]);
		},
	});
};

// ==================== TICKET FILE MUTATIONS ====================

/**
 * Upload a ticket attachment.
 * POST /tickets/{ticketId}/files
 */
export const useUploadTicketFile = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			ticketId,
			file,
			organizationId,
			description,
			entityAttachmentRequirementTagId,
			onUploadProgress,
		}: {
			ticketId: string;
			file: File | Blob;
			organizationId?: string;
			description?: string;
			entityAttachmentRequirementTagId?: string | null;
			onUploadProgress?: (percent: number) => void;
		}) =>
			ticketService.uploadTicketFile(ticketId, file, {
				organizationId,
				description,
				entityAttachmentRequirementTagId,
				onUploadProgress,
			}),
		onSuccess: (_result, { ticketId }) => {
			queryClient.invalidateQueries({
				queryKey: ticketKeys.ticketFiles(ticketId),
			});
		},
	});
};

/**
 * Delete a ticket attachment.
 * DELETE /tickets/{ticketId}/files/{fileId}
 */
export const useDeleteTicketFile = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			ticketId,
			fileId,
			organizationId,
		}: {
			ticketId: string;
			fileId: string;
			organizationId?: string;
		}) => ticketService.deleteTicketFile(ticketId, fileId, { organizationId }),
		onSuccess: (_result, { ticketId }) => {
			queryClient.invalidateQueries({
				queryKey: ticketKeys.ticketFiles(ticketId),
			});
		},
	});
};

/**
 * Rename / update a ticket attachment.
 * PUT /tickets/{ticketId}/files/{ticketFileId}
 */
export const useEditTicketFile = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			ticketId,
			fileId,
			name,
			description,
			organizationId,
		}: {
			ticketId: string;
			fileId: string;
			name?: string | null;
			description?: string | null;
			organizationId?: string;
		}) =>
			ticketService.editTicketFile(
				ticketId,
				fileId,
				{ name, description },
				{ organizationId },
			),
		onSuccess: (_result, { ticketId }) => {
			queryClient.invalidateQueries({
				queryKey: ticketKeys.ticketFiles(ticketId),
			});
		},
	});
};

// ==================== TICKET NOTE MUTATIONS ====================

/**
 * Create a ticket note.
 * POST /tickets/{ticketId}/notes
 */
export const useCreateTicketNote = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			ticketId,
			text,
			organizationId,
		}: {
			ticketId: string;
			text: string;
			organizationId?: string;
		}) =>
			ticketService.createTicketNote(ticketId, { text }, { organizationId }),
		onSuccess: (_result, { ticketId }) => {
			queryClient.invalidateQueries({
				queryKey: ticketKeys.ticketNotes(ticketId),
			});
		},
	});
};

/**
 * Update a ticket note.
 * PUT /tickets/{ticketId}/notes/{noteId}
 */
export const useUpdateTicketNote = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			ticketId,
			noteId,
			text,
			organizationId,
		}: {
			ticketId: string;
			noteId: string;
			text: string;
			organizationId?: string;
		}) =>
			ticketService.updateTicketNote(
				ticketId,
				noteId,
				{ text },
				{ organizationId },
			),
		onSuccess: (_result, { ticketId }) => {
			queryClient.invalidateQueries({
				queryKey: ticketKeys.ticketNotes(ticketId),
			});
		},
	});
};

/**
 * Delete a ticket note.
 * DELETE /tickets/{ticketId}/notes/{noteId}
 */
export const useDeleteTicketNote = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			ticketId,
			noteId,
			organizationId,
		}: {
			ticketId: string;
			noteId: string;
			organizationId?: string;
		}) => ticketService.deleteTicketNote(ticketId, noteId, { organizationId }),
		onSuccess: (_result, { ticketId }) => {
			queryClient.invalidateQueries({
				queryKey: ticketKeys.ticketNotes(ticketId),
			});
		},
	});
};

// ==================== GROUPED EXPORTS ====================

/**
 * All ticket mutations grouped by category
 */
export const ticketMutations = {
	create: useCreateTicket,
	previewFromText: usePreviewTicketFromText,
	createFromText: useCreateTicketFromText,
	createTicketType: useCreateTicketType,
	duplicateTicketType: useDuplicateTicketType,
	updateTicketType: useUpdateTicketType,
	update: useUpdateTicket,
	updateStatus: useUpdateTicketStatus,
	delete: useDeleteTicket,
	exportTickets: useExportTickets,
	exportTicketTypes: useExportTicketTypes,
	deleteTicketType: useDeleteTicketType,
	linkAsset: useLinkTicketAsset,
	unlinkAsset: useUnlinkTicketAsset,
	linkRecord: useLinkTicketRecord,
	unlinkRecord: useUnlinkTicketRecord,
	uploadFile: useUploadTicketFile,
	deleteFile: useDeleteTicketFile,
	editFile: useEditTicketFile,
	createNote: useCreateTicketNote,
	updateNote: useUpdateTicketNote,
	deleteNote: useDeleteTicketNote,
} as const;
